import { ReactNode, useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "./AdminSidebar";
import { UserMenu } from "@/components/dashboard/UserMenu";
import { Badge } from "@/components/ui/badge";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { Footer } from "@/components/Footer";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface AdminLayoutProps {
  children: ReactNode;
  title?: string;
}

export function AdminLayout({ children, title }: AdminLayoutProps) {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("first_name, profile_picture_url")
        .eq("id", user.id)
        .maybeSingle();

      if (data) setUserProfile(data);
    };

    fetchUserProfile();
  }, [user]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <div className="flex-1 flex flex-col">
          <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-16 items-center px-4">
              {/* Left section */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <SidebarTrigger>
                  <Menu className="h-5 w-5" />
                </SidebarTrigger>
                {title && (
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold">{title}</h2>
                    <Badge variant="destructive">Admin</Badge>
                  </div>
                )}
              </div>

              {/* Center branding */}
              <div className="flex-1 flex justify-center">
                <span className="text-2xl font-black bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent drop-shadow-sm">
                  MAS Matrimonial
                </span>
              </div>

              {/* Right section */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <FeedbackDialog />
                <UserMenu
                  profilePicture={userProfile?.profile_picture_url}
                  firstName={userProfile?.first_name}
                />
              </div>
            </div>
          </header>
          <main className="flex-1 p-6 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            {children}
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
}
